import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ga } from "../lib/analytics";
import { Gift, Copy, Share2, MessageCircle, IndianRupee, Users } from "lucide-react";

/**
 * Refer & Earn card — fetches the current patient's referral code +
 * stats, exposes copy + WhatsApp share. Variants:
 *   - "thankyou": full hero card with confetti emoji + green theme
 *   - "compact":  inline card for the patient dashboard
 */
export default function ReferralCard({ variant = "compact" }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/patients/me/referral").then((r) => { if (alive) setData(r.data); })
      .catch((e) => console.warn("[referral]", e?.message || e));
    return () => { alive = false; };
  }, []);

  if (!data) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      toast.success("Referral link copied");
      ga.referralShared({ channel: "copy", code: data.code, variant });
    } catch { toast.error("Could not copy"); }
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(data.share_text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    ga.referralShared({ channel: "whatsapp", code: data.code, variant });
  };

  const shareNative = async () => {
    if (!navigator.share) { copyLink(); return; }
    setBusy(true);
    try {
      await navigator.share({
        title: "NeuroOpinion India",
        text: data.share_text,
        url: data.link,
      });
      ga.referralShared({ channel: "native", code: data.code, variant });
    } catch (e) {
      if (e?.name !== "AbortError") console.warn("[share]", e?.message || e);
    } finally { setBusy(false); }
  };

  const reward = data.reward_rupees;
  const stats = data.stats || {};

  if (variant === "thankyou") {
    return (
      <Card className="mt-5 p-5 sm:p-6 border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 overflow-hidden relative" data-testid="referral-card-thankyou">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-200/40 blur-2xl pointer-events-none" aria-hidden />
        <div className="relative">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white grid place-items-center shadow-md shadow-amber-500/30">
              <Gift className="w-6 h-6"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">Refer &amp; earn</div>
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mt-1 leading-tight">
                Help a friend, earn ₹{reward} credit
              </h3>
              <p className="text-sm text-slate-700/90 mt-1 leading-relaxed">
                Share your code <span className="font-mono font-bold text-amber-800 bg-white px-1.5 py-0.5 rounded border border-amber-200">{data.code}</span>.
                When a friend books their first consultation, <b>you both get ₹{reward} off</b>.
              </p>
            </div>
          </div>

          <div className="mt-4 flex items-stretch gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-white rounded-lg border border-amber-200 px-3 py-2.5">
              <span className="text-xs text-slate-500 font-medium shrink-0">Your link</span>
              <span className="text-xs sm:text-sm font-mono text-slate-900 truncate flex-1" data-testid="referral-link">{data.link}</span>
            </div>
            <Button onClick={copyLink} variant="outline"
                    className="h-11 border-amber-300 text-amber-800 hover:bg-amber-100"
                    data-testid="referral-copy-btn">
              <Copy className="w-4 h-4 mr-1"/> Copy
            </Button>
            <Button onClick={shareWhatsApp}
                    className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="referral-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-1"/> WhatsApp
            </Button>
            {typeof navigator !== "undefined" && navigator.share && (
              <Button onClick={shareNative} disabled={busy} variant="outline"
                      className="h-11 border-slate-300"
                      data-testid="referral-share-btn">
                <Share2 className="w-4 h-4"/>
              </Button>
            )}
          </div>

          {(stats.referrals_count > 0 || stats.pending_credit > 0) && (
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white/80 border border-amber-200 px-2 py-2">
                <div className="text-[9px] uppercase tracking-wider text-amber-800/80 font-bold">Friends</div>
                <div className="font-heading text-base font-bold text-slate-900 inline-flex items-center gap-1 justify-center">
                  <Users className="w-3.5 h-3.5"/> {stats.referrals_count}
                </div>
              </div>
              <div className="rounded-lg bg-white/80 border border-amber-200 px-2 py-2">
                <div className="text-[9px] uppercase tracking-wider text-amber-800/80 font-bold">Earned</div>
                <div className="font-heading text-base font-bold text-emerald-700 inline-flex items-center gap-0.5 justify-center">
                  <IndianRupee className="w-3.5 h-3.5"/>{stats.total_earned}
                </div>
              </div>
              <div className="rounded-lg bg-white/80 border border-amber-200 px-2 py-2">
                <div className="text-[9px] uppercase tracking-wider text-amber-800/80 font-bold">Pending</div>
                <div className="font-heading text-base font-bold text-slate-900 inline-flex items-center gap-0.5 justify-center">
                  <IndianRupee className="w-3.5 h-3.5"/>{stats.pending_credit}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // compact variant for dashboard
  return (
    <Card className="p-4 sm:p-5 border-amber-200 bg-amber-50/60" data-testid="referral-card-compact">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center">
          <Gift className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">Refer &amp; earn</div>
          <div className="font-heading text-sm font-semibold text-slate-900 mt-0.5">
            Both get ₹{reward} off · code <span className="font-mono text-amber-800">{data.code}</span>
          </div>
          {(stats.referrals_count > 0 || stats.pending_credit > 0) && (
            <div className="text-[11px] text-slate-600 mt-0.5">
              {stats.referrals_count} invited · ₹{stats.total_earned} earned · ₹{stats.pending_credit} pending
            </div>
          )}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" onClick={copyLink} variant="outline"
                  className="h-8 px-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                  data-testid="referral-copy-btn-compact">
            <Copy className="w-3.5 h-3.5"/>
          </Button>
          <Button size="sm" onClick={shareWhatsApp}
                  className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="referral-whatsapp-btn-compact">
            <MessageCircle className="w-3.5 h-3.5"/>
          </Button>
        </div>
      </div>
    </Card>
  );
}
